/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: platform-branch
 */
import 'react-native-gesture-handler';
// Session reset registrations — must load before any screen/store renders.
// Centralized here to avoid side-effect imports in public-api barrels (require cycle crashes).
import './src/init/sessionResetRegistrations';
import './src/init/authCallbacksRegistration';
import { registerRootNavigationRef, resetShellStackOnBranchChange } from './src/shell/navigation/shellNavigationReset';
import { bindResetShellStack } from './src/auth/store/branchChange';

bindResetShellStack(resetShellStackOnBranchChange);
import { NavigationContainer, type NavigationContainerRef, type ParamListBase } from '@react-navigation/native';
import { StatusBar } from 'expo-status-bar';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  type ViewStyle,
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { SafeAreaProvider, SafeAreaView, SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { AppNavigator } from './src/shell/navigation/AppNavigator';
import { SentryErrorBoundary } from './src/shared/sentry/SentryErrorBoundary';
import { navigationTheme } from './src/shell/navigation/navigationTheme';
import { colors } from './src/shared/theme/public-api';
import { ensureTicketFonts } from './src/shared/ticket/ensureTicketFonts';
import { useInitDatabase } from './src/shared/hooks/useInitDatabase';
import { useSyncCoordinator } from './src/sync/hooks/useSyncCoordinator';
import { useSyncRealtimeRefresh } from './src/sync/hooks/useSyncRealtimeRefresh';
import { PlanEntitlementsProvider, usePlanEntitlements } from './src/shell/providers/PlanEntitlementsProvider';
import { ClientPresenceReporter } from './src/shell/providers/ClientPresenceReporter';
import { AccountSetupProvider, useAccountSetup } from './src/shell/providers/AccountSetupProvider';
import { GlobalFeatureFlagsProvider, useGlobalFeatureFlags } from './src/shell/providers/GlobalFeatureFlagsProvider';
import { useIsCurrentUserEmployee } from './src/auth/hooks/useIsCurrentUserEmployee';
import { useAuthStore } from './src/auth/store/useAuthStore';
import { useSessionStore } from './src/shell/session/store/useSessionStore';
import { useBusinessProfileStore } from './src/shell/session/store/useBusinessProfileStore';
import { LoginScreen } from './src/auth/screens/LoginScreen';
import { PasswordRecoveryScreen } from './src/auth/screens/PasswordRecoveryScreen';
import { PasswordRecoveryFailedScreen } from './src/auth/screens/PasswordRecoveryFailedScreen';
import { BranchSetupScreen } from './src/auth/screens/OnboardingScreen';
import { OfflineSetupRequired } from './src/auth/screens/OfflineSetupRequired';
import { WaitingForInvitationScreen } from './src/auth/screens/WaitingForInvitationScreen';
import { PendingInvitationScreen } from './src/settings/screens/PendingInvitationScreen';
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { logout } from './src/auth/application/auth';
import { needsForceUpdate } from './src/shell/session/appVersionGate';
import { ForceUpdateScreen } from './src/shell/screens/ForceUpdateScreen';
import { SupportUnreadProvider } from './src/shell/providers/SupportUnreadProvider';
import { EmployeePermissionsFreshnessProvider } from './src/shell/providers/EmployeePermissionsFreshnessProvider';
import { PendingOrdersCountProvider } from './src/shell/providers/PendingOrdersCountProvider';
import { NewOrderAlertProvider } from './src/shell/providers/NewOrderAlertProvider';
import {
  clearPasswordRecoveryFlowMarker,
  getPasswordRecoveryFlowUserId,
  markPasswordRecoveryFlowStarted,
} from './src/auth/application/passwordRecoveryWebStorage';
import { isPasswordRecoveryUrl } from './src/auth/application/sso';
import { usePasswordRecoveryGate } from './src/auth/hooks/usePasswordRecoveryGate';
import { captureWebEmailConfirmIntentFromLocation } from './src/auth/application/emailConfirmToast';
import { consumeMercadoPagoOAuthPostRedirect } from './src/mercadopago/infra/mercadopagoOAuthCallback';
import { useOfflineBootstrapEvidence } from './src/shell/session/hooks-offline/useOfflineBootstrapEvidence';
import { OfflineConnectionBanner } from './src/shell/banners/offline/OfflineConnectionBanner';
import { WebStaleTabBanner } from './src/shell/banners/offline/WebStaleTabBanner';
import { WebUpdateOverlay } from './src/shell/versioning/WebUpdateOverlay';
import { AppTopBanners } from './src/shell/banners/components/AppTopBanners';
import { PilotDeployBanner } from './src/shell/banners/components/PilotDeployBanner';
import { appRootSafeAreaEdges } from './src/shell/navigation/appRootSafeArea';
import { useLocalDataPrimer } from './src/shell/hooks/useLocalDataPrimer';
import { useNetInfoReconnect } from './src/shell/hooks/useNetInfoReconnect';
import { useWebDocumentStyle } from './src/shell/hooks/useWebDocumentStyle';
import { TopChromeOffsetProvider } from './src/shell/navigation/TopChromeOffsetContext';
import { PlanLapsedModal } from './src/shell/billing/components/PlanLapsedModal';
import { usePlanLapsedPrompts } from './src/shell/billing/hooks/usePlanLapsedPrompts';
import { readBillingFlagFromLocation } from './src/shell/billing/billingWebHandoff';
import { isBillingEnabled } from './src/shell/billing/isBillingEnabled';
import { EntitlementsGapBanner } from './src/shell/banners/components/EntitlementsGapBanner';
import { ImpersonationBanner } from './src/shell/banners/components/ImpersonationBanner';
import { useImpersonationLifecycle } from './src/shell/support/useImpersonationLifecycle';
import Toast from 'react-native-toast-message';
import { appToastConfig, toastTopOffset } from './src/shared/ui/appToast';
import { useSupabaseSessionKeepAlive } from './src/auth/hooks/useSupabaseSessionKeepAlive';
import { useAuthSessionHydration } from './src/auth/hooks/useAuthSessionHydration';
import { useBranchBootstrap } from './src/shell/hooks/useBranchBootstrap';
import { useDeepLinkRouter } from './src/shell/hooks/useDeepLinkRouter';
import { useBillingRelay } from './src/shell/hooks/useBillingRelay';
import { useOperationalGate } from './src/shell/hooks/useOperationalGate';
import { BrandWatermark } from './src/shell/brand/BrandWatermark';
import {
  captureMarketingPlanIntent,
  flushEphemeralPlanIntentAfterAuth,
  type PaidPlanCode,
} from './src/shell/feature-flags/planIntentService';
import { useSingleInstanceGuard } from './src/sync/hooks/useSingleInstanceGuard';
import { SingleInstanceBlocker } from './src/shell/banners/components/SingleInstanceBlocker';
import * as Sentry from '@sentry/react-native';
import { setSentryGlobalTags } from './src/shared/sentry/sentryUser';
import { sentryBeforeSend } from './src/shared/sentry/sentryBeforeSend';
import { initGA4 } from './src/shared/analytics/ga4';

// Web carga la misma tipografía que los módulos Square antes del primer render.
ensureTicketFonts();

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  enabled: !__DEV__ && !!process.env.EXPO_PUBLIC_SENTRY_DSN,
  environment: process.env.EXPO_PUBLIC_APP_ENV ?? 'production',

  // Adds more context data to events (IP address, cookies, user, etc.)
  // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
  sendDefaultPii: true,

  // Enable Logs
  enableLogs: false,

  // Performance monitoring (sample bajo para no consumir cuota de mas)
  tracesSampleRate: 0.15,

  beforeSend: sentryBeforeSend,
});

setSentryGlobalTags();

// GA4 — solo web (PWA app.vuelto.ar). No-op en native.
initGA4();

/** Si el gate no sale en este tiempo, ofrecemos reintento / logout (red colgada, bootstrap lento). */
const BOOT_STUCK_MS = 20_000;

function AppBootScreen({
  accessibilityLabel = 'Preparando Vuelto',
  hint = 'Cargando tu negocio…',
  onRetry,
}: {
  accessibilityLabel?: string;
  hint?: string;
  /** Tras ~20s: reintentar bootstrap sin matar la sesión. */
  onRetry?: () => void | Promise<void>;
}) {
  const [stuck, setStuck] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    setStuck(false);
    const t = setTimeout(() => setStuck(true), BOOT_STUCK_MS);
    return () => clearTimeout(t);
  }, [accessibilityLabel, hint, onRetry, retryNonce]);

  const handleRetry = useCallback(() => {
    if (!onRetry || retrying) return;
    setRetrying(true);
    void Promise.resolve(onRetry()).finally(() => {
      setRetrying(false);
      setRetryNonce((n) => n + 1);
    });
  }, [onRetry, retrying]);

  return (
    <View
      style={styles.bootWrap}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="progressbar"
    >
      <BrandWatermark variant="boot" />
      <View style={[styles.bootCard, Platform.OS === 'web' ? styles.bootCardWeb : null]}>
        <View style={styles.bootBrandRow}>
          <View style={styles.bootMark}>
            <ActivityIndicator size="small" color={colors.accent} />
          </View>
          <View style={styles.bootCopy}>
            <Text style={styles.bootKicker}>Vuelto</Text>
            <Text style={styles.bootTitle}>Preparando la app</Text>
          </View>
        </View>
        <Text style={styles.bootHint}>
          {stuck ? 'Está tardando más de lo usual. Probá de nuevo o cerrá sesión.' : hint}
        </Text>
        <View style={styles.bootRail} accessible={false}>
          <View style={styles.bootRailFill} />
        </View>
        {stuck ? (
          <View style={styles.bootActions}>
            {onRetry ? (
              <TouchableOpacity
                style={[styles.bootRetryBtn, retrying && styles.bootRetryBtnDisabled]}
                onPress={handleRetry}
                disabled={retrying}
                accessibilityRole="button"
                accessibilityLabel="Reintentar carga"
                testID="app-boot.retry"
              >
                {retrying ? (
                  <ActivityIndicator size="small" color={colors.background} />
                ) : (
                  <Text style={styles.bootRetryLabel}>Reintentar</Text>
                )}
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              onPress={() => void logout()}
              disabled={retrying}
              accessibilityRole="button"
              accessibilityLabel="Cerrar sesión"
              testID="app-boot.logout"
            >
              <Text style={styles.bootLogoutLabel}>Cerrar sesión</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
    </View>
  );
}

/**
 * En web, `Modal` se monta como hermano de `#root` en `document.body`; el toast dentro del árbol
 * principal queda por debajo. Portal a `body` + z-index alto lo deja por encima de modales.
 */
function AppToastHost() {
  const insets = useContext(SafeAreaInsetsContext) ?? { top: 0, bottom: 0, left: 0, right: 0 };
  const toastEl = <Toast config={appToastConfig} topOffset={toastTopOffset(insets.top)} />;

  if (Platform.OS === 'web') {
    if (typeof document === 'undefined') {
      return null;
    }
    const { createPortal } = require('react-dom') as typeof import('react-dom');
    return createPortal(
      <View style={[styles.toastPortalRoot, styles.toastLayerPointerEvents]} collapsable={false}>
        {toastEl}
      </View>,
      document.body
    );
  }

  return (
    <View style={[styles.toastLayerOverlay, styles.toastLayerPointerEvents]} collapsable={false}>
      {toastEl}
    </View>
  );
}

function AppShell() {
  const instanceGuard = useSingleInstanceGuard();

  if (instanceGuard === 'blocked') {
    return <SingleInstanceBlocker />;
  }

  return <AppShellContent />;
}

function AppShellContent() {
  useWebDocumentStyle();

  useInitDatabase();
  // Heartbeat versión/plataforma (cooldown 6h) cuando hay sesión + org.
  /** Sync en background: la cola SQLite serializada en web cede prioridad a listas activas (POS/catálogo). */
  useSyncCoordinator();
  const impersonation = useImpersonationLifecycle();
  const { session } = useAuthStore();
  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const hasBusinessProfile = businessProfile != null;
  const {
    requiresOwnerOnboarding,
    organizationClosedAt,
    pendingEmployeeInvitation,
    awaitingEmployeeInvitation,
    resolved: accountSetupResolved,
    refreshAccountSetup,
  } = useAccountSetup();
  useSupabaseSessionKeepAlive(Boolean(session?.user));
  const {
    entitlements,
    loading: entLoading,
    refresh: refreshEntitlements,
  } = usePlanEntitlements();
  const {
    flags: globalFeatureFlags,
    loading: globalFeatureFlagsLoading,
  } = useGlobalFeatureFlags();
  const isEmployee = useIsCurrentUserEmployee();
  const branches = useSessionStore((s) => s.branches);
  const { evidence: offlineBootstrapEvidence, refresh: refreshOfflineBootstrapEvidence } =
    useOfflineBootstrapEvidence(session?.user?.id);
  useSyncRealtimeRefresh(
    Boolean(session?.user) &&
      (requiresOwnerOnboarding === false || offlineBootstrapEvidence === true),
  );
  const [offlineRetrying, setOfflineRetrying] = useState(false);
  const [entitlementsRetrying, setEntitlementsRetrying] = useState(false);
  /** Mantener BranchSetup tras crear org hasta CTA de «Listo» (paso 3). */
  const [holdOnboardingSuccess, setHoldOnboardingSuccess] = useState(false);
  const [planParam, setPlanParam] = useState<string | null>(null);
  const [billingFlag, setBillingFlag] = useState(() => readBillingFlagFromLocation());
  const mpOAuthNavigationDoneRef = useRef(false);
  const navigationRef = useRef<NavigationContainerRef<ParamListBase>>(null);
  const passwordRecoveryAbandonedSignOut = useRef(false);
  const passwordRecoveryPathname = useRef<string | null>(null);
  /** Solo Expo web (app.vuelto PWA); en iOS/Android el recovery se abre en el navegador. */
  const [passwordRecoveryUrlIntent, setPasswordRecoveryUrlIntent] = useState(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return isPasswordRecoveryUrl(window.location.href);
    }
    return false;
  });
  const {
    status: passwordRecoveryStatus,
    dismissFailed: dismissPasswordRecoveryFailed,
    completeRecoveryUi,
  } = usePasswordRecoveryGate(passwordRecoveryUrlIntent);

  const handlePlanIntent = useCallback((plan: PaidPlanCode) => {
    setPlanParam(plan);
    // Solo buffer de marketing: pending durable se escribe al iniciar checkout MP.
    captureMarketingPlanIntent(plan);
  }, []);

  const navigateToActivateCreceScreen = useCallback((planCode: PaidPlanCode = 'crece') => {
    navigationRef.current?.navigate('AppShell', { screen: 'ActivateCrece', params: { planCode } });
  }, []);

  const navigateToCreceRelay = useCallback((
    target: 'ActivateCrece' | 'CreceSuccess',
    planCode: PaidPlanCode | null,
  ) => {
    navigationRef.current?.navigate('AppShell', {
      screen: target,
      params: target === 'ActivateCrece' && planCode ? { planCode } : undefined,
    });
  }, []);

  const navigateToMercadoPagoSettingsOnce = useCallback(() => {
    if (mpOAuthNavigationDoneRef.current) return;
    const pending = consumeMercadoPagoOAuthPostRedirect();
    if (!pending) return;
    mpOAuthNavigationDoneRef.current = true;
    navigationRef.current?.navigate('AppShell', { screen: pending.shellRoute });
  }, []);

  const navigateToSupportTicket = useCallback((ticketId: string) => {
    navigationRef.current?.navigate('AppShell', {
      screen: 'MainTabs',
      params: { screen: 'SupportReports', params: { ticketId } },
    });
  }, []);

  /** Reintento desde «Preparando la app» sin volver a `resolved: false` (evita loop de flash). */
  const retryOperationalBoot = useCallback(async () => {
    try {
      await Promise.all([
        refreshAccountSetup({ force: true, background: true }),
        refreshEntitlements({ force: true }),
      ]);
    } catch {
      // El gate / OfflineSetup muestran el siguiente estado.
    } finally {
      refreshOfflineBootstrapEvidence();
    }
  }, [refreshAccountSetup, refreshEntitlements, refreshOfflineBootstrapEvidence]);

  /** Plan/entitlements cargados; no sustituye el gate de onboarding del servidor. */
  const entitlementsOperational = Boolean(entitlements?.organization_id);
  const hasOrgAndBranches = entitlementsOperational && (entitlements?.branch_count ?? 0) > 0;

  useLayoutEffect(() => {
    captureWebEmailConfirmIntentFromLocation();
  }, []);

  /**
   * Web: el enlace de recovery persiste sesión (Supabase `detectSessionInUrl`). Si el usuario
   * abre el mail, no completa el form y vuelve a otra ruta, cerramos sesión; el marcador
   * `localStorage` se limpia en logout, login, OAuth, etc.
   * Solo aplica cuando el gate confirmó recovery (`ready`), no con sesión normal + URL rara.
   */
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !session?.user) {
      return;
    }
    if (passwordRecoveryStatus !== 'ready') {
      return;
    }
    const pathname = window.location.pathname;
    const href = window.location.href;
    if (isPasswordRecoveryUrl(href)) {
      passwordRecoveryPathname.current = pathname;
      markPasswordRecoveryFlowStarted(session.user.id);
      return;
    }
    const pending = getPasswordRecoveryFlowUserId();
    if (pending && pending !== session.user.id) {
      clearPasswordRecoveryFlowMarker();
      passwordRecoveryPathname.current = null;
      return;
    }
    if (pending && pending === session.user.id) {
      if (passwordRecoveryPathname.current === null) {
        passwordRecoveryPathname.current = pathname;
        return;
      }
      if (passwordRecoveryPathname.current === pathname) {
        return;
      }
      if (passwordRecoveryAbandonedSignOut.current) {
        return;
      }
      passwordRecoveryAbandonedSignOut.current = true;
      void (async () => {
        try {
          await logout('password_recovery_abandoned');
          passwordRecoveryPathname.current = null;
          setPasswordRecoveryUrlIntent(false);
          completeRecoveryUi();
        } finally {
          passwordRecoveryAbandonedSignOut.current = false;
        }
      })();
    }
  }, [session, passwordRecoveryStatus, completeRecoveryUi]);

  useDeepLinkRouter({
    onPasswordRecoveryUrl: () => setPasswordRecoveryUrlIntent(true),
    onPlanIntent: handlePlanIntent,
    onBillingFlag: () => setBillingFlag(true),
    onMercadoPagoSettings: navigateToMercadoPagoSettingsOnce,
    onSupportTicketId: navigateToSupportTicket,
  });

  const initialAuthReady = useAuthSessionHydration();

  const {
    initialBranchSelectionReady,
    employeeAccessReady,
    appUpdateConfig,
  } = useBranchBootstrap({
    userId: session?.user?.id,
    accountSetupResolved,
    requiresOwnerOnboarding,
    hasOrgAndBranches,
    pendingEmployeeInvitation,
  });
  useEffect(() => {
    if (!session?.user) {
      return;
    }
    // Tras login/signup: abrir Activar plan Crece una vez si venía de marketing.
    // No releer user_metadata.pending_plan acá — eso secuestraba Inicio en cada cold start.
    void flushEphemeralPlanIntentAfterAuth().then((plan) => {
      if (plan) {
        setPlanParam(plan);
        setBillingFlag(true);
      }
    });
  }, [session?.user?.id]);

  useLocalDataPrimer(session?.user?.id);

  useNetInfoReconnect(session?.user?.id, refreshEntitlements);

  const {
    needsOnboarding,
    needsPendingInvitation,
    needsAwaitingInvitation,
    needsOfflineSetupHint,
    accountOperational,
    accountOperationalIncludingOffline,
    bootstrapping,
  } = useOperationalGate({
    userId: session?.user?.id,
    session: !!session,
    accountSetupResolved,
    requiresOwnerOnboarding,
    pendingEmployeeInvitation,
    awaitingEmployeeInvitation,
    hasOrgAndBranches,
    hasBusinessProfile,
    offlineBootstrapEvidence,
    refreshOfflineBootstrapEvidence,
    refreshAccountSetup,
    initialAuthReady,
    initialBranchSelectionReady,
    employeeAccessReady,
  });

  /** Relay a portal de planes / Billing: no redirigir mientras el rol carga (`isEmployee === null`) ni durante onboarding de sucursal. */
  const canRelayToSubscriptionPortal = isEmployee !== null && !needsOnboarding;

  const navigateToCreceOnce = useBillingRelay({
    session,
    bootstrapping,
    canRelayToSubscriptionPortal,
    entitlements,
    isEmployee,
    planParam,
    billingFlag,
    setPlanParam,
    setBillingFlag,
    onNavigateToCrece: navigateToCreceRelay,
  });
  void navigateToCreceOnce;

  const planLapsedPrompts = usePlanLapsedPrompts(navigateToActivateCreceScreen);

  const handleEntitlementsRetry = () => {
    setEntitlementsRetrying(true);
    void refreshEntitlements()
      .catch(() => {})
      .finally(() => setEntitlementsRetrying(false));
  };

  useEffect(() => {
    if (!session || bootstrapping || Platform.OS !== 'web') {
      return;
    }
    navigateToMercadoPagoSettingsOnce();
  }, [session, bootstrapping, navigateToMercadoPagoSettingsOnce]);

  const mainContent = (() => {
    if (passwordRecoveryStatus === 'validating') {
      return (
        <AppBootScreen
          accessibilityLabel="Preparando recuperación de contraseña"
          hint="Validando el enlace de recuperación..."
        />
      );
    }
    if (passwordRecoveryStatus === 'failed') {
      return (
        <PasswordRecoveryFailedScreen
          onBackToLogin={() => {
            dismissPasswordRecoveryFailed();
            setPasswordRecoveryUrlIntent(false);
          }}
        />
      );
    }
    if (passwordRecoveryStatus === 'ready') {
      return (
        <PasswordRecoveryScreen
          onComplete={() => {
            completeRecoveryUi();
            setPasswordRecoveryUrlIntent(false);
          }}
        />
      );
    }
    if (bootstrapping) {
      return (
        <AppBootScreen
          accessibilityLabel="Cargando cuenta"
          onRetry={retryOperationalBoot}
        />
      );
    }
    if (appUpdateConfig && needsForceUpdate(appUpdateConfig)) {
      return <ForceUpdateScreen config={appUpdateConfig} />;
    }
    if (needsPendingInvitation && pendingEmployeeInvitation) {
      return (
        <PendingInvitationScreen
          invitation={pendingEmployeeInvitation}
          onAccepted={async () => {
            await refreshAccountSetup({ force: true, background: true });
            await refreshEntitlements({ force: true });
          }}
          onRejected={async () => {
            await refreshAccountSetup({ force: true, background: true });
          }}
        />
      );
    }
    if (needsAwaitingInvitation) {
      return (
        <WaitingForInvitationScreen
          onRefresh={async () => {
            await refreshAccountSetup({ force: true, background: true });
            await refreshEntitlements({ force: true });
          }}
        />
      );
    }
    if (needsOnboarding || holdOnboardingSuccess) {
      return (
        <BranchSetupScreen
          onCreateStarted={() => setHoldOnboardingSuccess(true)}
          onComplete={() => {
            // Setup + entitlements ya se refreshearon en BranchSetupScreen.handleComplete
            // con background:true — no forzar resolved:false acá (flash de «Preparando»).
            setHoldOnboardingSuccess(false);
          }}
        />
      );
    }
    if (needsOfflineSetupHint) {
      return (
        <OfflineSetupRequired
          retrying={offlineRetrying}
          onRetry={() => {
            setOfflineRetrying(true);
            void Promise.all([
              refreshAccountSetup({ force: true, background: true }),
              refreshEntitlements({ force: true }),
            ])
              .catch(() => {})
              .finally(() => {
                refreshOfflineBootstrapEvidence();
                setOfflineRetrying(false);
              });
          }}
        />
      );
    }
    if (session && accountOperationalIncludingOffline) {
      return (
        <SupportUnreadProvider>
          <EmployeePermissionsFreshnessProvider>
            <PendingOrdersCountProvider>
              <NewOrderAlertProvider>
                <SentryErrorBoundary moduleName="app-navigator">
                  <AppNavigator
                    entitlements={entitlements}
                    globalFeatureFlags={globalFeatureFlags}
                    requiresOwnerOnboarding={requiresOwnerOnboarding}
                    organizationClosedAt={organizationClosedAt}
                  />
                </SentryErrorBoundary>
              </NewOrderAlertProvider>
            </PendingOrdersCountProvider>
          </EmployeePermissionsFreshnessProvider>
        </SupportUnreadProvider>
      );
    }
    return <LoginScreen />;
  })();

  const entitlementsGapVisible =
    !!session &&
    branches.length > 0 &&
    !entLoading &&
    entitlements === null &&
    !bootstrapping &&
    !needsOnboarding &&
    !needsOfflineSetupHint;

  return (
    <NavigationContainer
      ref={(ref) => {
        navigationRef.current = ref as NavigationContainerRef<ParamListBase> | null;
        registerRootNavigationRef(ref as NavigationContainerRef<ParamListBase> | null);
      }}
      theme={navigationTheme}
      documentTitle={{
        enabled: true,
        formatter: (options) => {
          const t = options?.title;
          if (typeof t === 'string' && t.trim() !== '') {
            return `${t} · Vuelto`;
          }
          return 'Vuelto Negocio';
        },
      }}
    >
      <SafeAreaView style={styles.container} edges={appRootSafeAreaEdges()}>
        <ClientPresenceReporter />
        <TopChromeOffsetProvider>
          {session && !bootstrapping && !needsOfflineSetupHint ? (
            <AppTopBanners>
              <PilotDeployBanner />
              {impersonation.active ? (
                <ImpersonationBanner
                  targetEmail={impersonation.targetEmail}
                  expiresAtLabel={impersonation.expiresAtLabel}
                  exiting={impersonation.exiting}
                  onExit={() => void impersonation.exitImpersonation()}
                />
              ) : null}
              <OfflineConnectionBanner />
              <WebStaleTabBanner />
              <EntitlementsGapBanner
                visible={entitlementsGapVisible && !bootstrapping}
                retrying={entitlementsRetrying}
                onRetry={handleEntitlementsRetry}
              />
            </AppTopBanners>
          ) : null}
          <View style={styles.shellScene}>{mainContent}</View>
          {session ? <WebUpdateOverlay /> : null}
        {/* billing disabled — free mode: PlanLapsedModal kept but never visible when billing off */}
        <PlanLapsedModal
          visible={
            isBillingEnabled() &&
            planLapsedPrompts.subscriptionLapsed.visible &&
            accountOperational &&
            !bootstrapping
          }
          copy={planLapsedPrompts.subscriptionLapsed.copy}
          busy={planLapsedPrompts.subscriptionLapsed.busy}
          onChoosePlan={() => void planLapsedPrompts.subscriptionLapsed.choosePlan()}
          onDismiss={() => void planLapsedPrompts.subscriptionLapsed.dismiss()}
        />
        <PlanLapsedModal
          visible={
            isBillingEnabled() &&
            planLapsedPrompts.trialExpired.visible &&
            accountOperational &&
            !bootstrapping
          }
          copy={planLapsedPrompts.trialExpired.copy}
          busy={planLapsedPrompts.trialExpired.busy}
          onChoosePlan={() => void planLapsedPrompts.trialExpired.choosePlan()}
          onDismiss={() => void planLapsedPrompts.trialExpired.dismiss()}
        />
        <StatusBar style="dark" />
        </TopChromeOffsetProvider>
      </SafeAreaView>
    </NavigationContainer>
  );
}

export default Sentry.wrap(function App() {
  return (
    <GestureHandlerRootView style={styles.root}>
      <KeyboardProvider>
        <SafeAreaProvider>
          <PlanEntitlementsProvider>
            <AccountSetupProvider>
              <GlobalFeatureFlagsProvider>
                <AppShell />
              </GlobalFeatureFlagsProvider>
            </AccountSetupProvider>
          </PlanEntitlementsProvider>
          <AppToastHost />
        </SafeAreaProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
});

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  container: {
    flex: 1,
    minHeight: 0,
    backgroundColor: colors.background,
  },
  /** Fills remaining viewport under optional top banners. */
  shellScene: {
    flex: 1,
    minHeight: 0,
  },
  /** Nativo: overlay sobre la app; los Modal nativos pueden seguir tapando (capa del SO). */
  toastLayerOverlay: {
    ...StyleSheet.absoluteFill,
    zIndex: 100000,
    elevation: 100,
  },
  /** Web: fijo sobre el viewport, por encima de RN Modal (portal en body). */
  toastPortalRoot: Platform.select<ViewStyle>({
    web: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 2147483646,
    } as unknown as ViewStyle,
    default: {},
  }),
  toastLayerPointerEvents: {
    pointerEvents: 'box-none',
  },
  bootWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
    paddingHorizontal: 24,
    overflow: 'hidden',
  },
  bootCard: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    padding: 18,
    gap: 14,
  },
  bootCardWeb: {
    position: 'relative',
    zIndex: 1,
  },
  bootBrandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  bootMark: {
    width: 46,
    height: 46,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.controlBorder,
    backgroundColor: colors.inputBackground,
  },
  bootCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  bootKicker: {
    color: colors.accent,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  bootTitle: {
    color: colors.textPrimary,
    fontSize: 17,
    lineHeight: 22,
    fontWeight: '800',
  },
  bootHint: {
    color: colors.textSecondary,
    fontSize: 13,
    lineHeight: 19,
    fontWeight: '600',
  },
  bootRail: {
    height: 4,
    borderRadius: 999,
    overflow: 'hidden',
    backgroundColor: colors.inputBackground,
  },
  bootRailFill: {
    width: '52%',
    height: '100%',
    borderRadius: 999,
    backgroundColor: colors.accent,
  },
  bootActions: {
    gap: 10,
    marginTop: 2,
  },
  bootRetryBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    borderRadius: 12,
    backgroundColor: colors.accent,
    paddingHorizontal: 16,
  },
  bootRetryBtnDisabled: {
    opacity: 0.7,
  },
  bootRetryLabel: {
    color: colors.background,
    fontSize: 15,
    fontWeight: '700',
  },
  bootLogoutLabel: {
    color: colors.textSecondary,
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    paddingVertical: 4,
  },
});
